import React from "react";
import { Scale, Eye, Lock, HelpCircle, Users2, Compass } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import FigureBlock from "../components/FigureBlock";

const FRAMEWORKS = [
    {
        icon: Scale,
        title: "The utilitarian ledger",
        body: "A security benefit weighed against a rights cost. The arithmetic fails not because the benefits are small, but because the costs are distributed onto the people least consulted — the misidentified, the over-watched, the communities whose faces the systems know least.",
    },
    {
        icon: Lock,
        title: "The rights position",
        body: "A deontological view holds some things impermissible regardless of outcome: treating the entire public as a suspect pool inverts the presumption of innocence. New Zealand's Bill of Rights — dignity, expression, unreasonable search — encodes exactly this (New Zealand Bill of Rights Act 1990).",
    },
    {
        icon: Compass,
        title: "The precautionary test",
        body: "Where harm is potentially irreversible (wrongful detention) and evidence of fairness is incomplete (overseas-trained datasets), precaution says wait. The Privacy Commissioner's 'amber light' verdict is a proportionality test in practice (Office of the Privacy Commissioner, 2025).",
    },
];

export default function Ethics() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-06"
                lines={["Ethical & social", <>issues — <i className="text-amber">who pays</i></>]}
                lede="Beneath the technical debate sit four ethical questions no accuracy threshold can answer: whether bias can ever be audited away, what anonymity in public is worth, who is accountable when software accuses, and how biometric data interacts with the sovereignty promises of Te Tiriti o Waitangi."
                meta={["SUBJECT: VALUES & ACCOUNTABILITY"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <div className="space-y-10">
                        {[
                            {
                                icon: Users2,
                                no: "6.1",
                                title: "Bias and discrimination",
                                body: "Bias is not a bug patching itself. The systems on the market were trained overwhelmingly on overseas faces; the Privacy Commissioner flagged the resulting potential for technical bias against Māori and Pacific people explicitly (Office of the Privacy Commissioner, 2025). In a country where Māori are already over-represented at every step of criminal justice contact, an error-prone identifier does not add bias neutrally — it multiplies it. Māori data sovereignty adds a further obligation: data about Māori is taonga, governed by commitments to Māori authority over its collection and use (Te Mana Raraunga, 2018).",
                            },
                            {
                                icon: Eye,
                                no: "6.2",
                                title: "Privacy and public anonymity",
                                body: "Historically, walking through a city meant practical anonymity: no one could catalogue where you were, let alone who you are. FRT ends that asymmetry by making every face a query. Surveillance culture normalises watching as a way of life until citizens self-censor by default (Lyon, 2018). Consent is structurally impossible here — one cannot opt out of having a face in public.",
                            },
                            {
                                icon: HelpCircle,
                                no: "6.3",
                                title: "Accountability gaps",
                                body: "When software accuses wrongly, who answers? The vendor who trained the model, the officer who trusted the match, the chain of command that approved deployment, or the threshold someone set at 90%? Without clear statutory allocation of responsibility, accountability diffuses until no one holds it — the lesson of both the 2018 and 2020 NZ trials (Independent Police Conduct Authority, 2020).",
                            },
                            {
                                icon: Lock,
                                no: "6.4",
                                title: "Function creep and permanence",
                                body: "Capabilities extend. A watchlist for violent offenders becomes one for repeat shoplifters; a tool for solving crimes becomes one for finding protest attendees. And the biometric database such systems imply is not a system that can be unwound: faces cannot be reissued like compromised passwords (Ajana, 2013).",
                            },
                        ].map((item) => (
                            <Reveal key={item.no}>
                                <div className="border border-white/10 p-7 sm:p-8" data-testid={`ethics-issue-${item.no.replace(".", "-")}`}>
                                    <div className="flex items-center gap-4 mb-4">
                                        <item.icon size={20} className="text-amber" />
                                        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-ash">{item.no}</span>
                                    </div>
                                    <h3 className="font-display text-2xl text-paper mb-4">{item.title}</h3>
                                    <p className="text-sm sm:text-base leading-relaxed text-ash">{item.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <div className="lg:pt-4 space-y-10">
                        <FigureBlock
                            num="7"
                            src="https://images.unsplash.com/photo-1592489427434-fd9696f768b0?q=80&w=1600&auto=format&fit=crop"
                            alt="People walking through a busy street, seen from behind"
                            caption="Public space has always been a commons. The ethical question FRT poses is whether citizenship there now requires exposure to permanent identification."
                            ratio="4/3"
                        />
                        <Reveal delay={0.1}>
                            <div className="border border-amber/30 bg-amber/[0.05] p-7">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-5">
                                    Three ethical lenses
                                </p>
                                <div className="space-y-6">
                                    {FRAMEWORKS.map((f) => (
                                        <div key={f.title} className="flex gap-4">
                                            <f.icon size={18} className="text-paper shrink-0 mt-1" />
                                            <div>
                                                <h4 className="text-paper font-medium mb-1.5">{f.title}</h4>
                                                <p className="text-sm leading-relaxed text-ash">{f.body}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
                    <Reveal>
                        <p className="font-display text-2xl sm:text-3xl text-paper leading-snug max-w-4xl">
                            The ethical bottom line of our research: <i className="text-amber">a technology that errs unevenly,
                            consents no one, and answers to no one outside the organisation using it</i> cannot be made
                            ethical by accuracy improvements alone. It needs structure — which is where the
                            governance options come in.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
